import React, { useEffect, useState } from "react";
import { LayoutGrid, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import WidgetRail, { RAIL_CUTOFF } from "./WidgetRail";

/**
 * Widgets on small screens.
 *
 * Below the rail cutover the widgets are removed from the page entirely and
 * live behind this button, which sits under the notification bell. Stacking
 * three widgets above the feed pushed the actual content down by a screenful;
 * a pop-out keeps the feed the whole page while leaving the widgets one tap
 * away.
 *
 * It takes the full width below the top bar — it is the widgets page on a
 * phone, not a peek at it — and slides in from the right edge under the button
 * that opened it rather than up from the bottom, so the motion points back at
 * its trigger.
 *
 * The drawer is a real overlay rather than a route, so opening it never loses
 * the reader's place in the feed.
 */
export default function WidgetsFlyout({ topOffset = 48 }) {
  const { t } = useTranslation("widgets");
  const [open, setOpen] = useState(false);
  const [small, setSmall] = useState(
    typeof window !== "undefined" ? window.innerWidth < RAIL_CUTOFF : false
  );

  useEffect(() => {
    const onResize = () => {
      const isSmall = window.innerWidth < RAIL_CUTOFF;
      setSmall(isSmall);
      // Growing past the cutover puts the widgets back on the page, so a drawer
      // left open would be showing them twice.
      if (!isSmall) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Locking the body prevents the feed scrolling behind the drawer on touch.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!small) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("flyout.show")}
        aria-expanded={open}
        className="fixed right-3 z-40 w-10 h-10 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center text-gray-600 hover:text-purple-700 hover:border-purple-300 transition-colors"
        style={{ top: topOffset + 10 }}
      >
        <LayoutGrid className="w-5 h-5" />
      </button>

      {open && (
        // Sits below the top bar rather than over it, so the nav and the
        // notification bell stay reachable while the panel is open.
        <div
          className="fixed inset-x-0 bottom-0 z-40"
          style={{ top: topOffset }}
        >
          {/* Not a close control any more — the panel covers it. It is still
              worth keeping: it dims the feed during the slide-in, which is the
              only moment any of it is visible. */}
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-black/30 menu-backdrop-in"
          />

          <aside
            role="dialog"
            aria-modal="true"
            aria-label={t("flyout.title")}
            className="absolute inset-0 bg-gray-50 shadow-2xl flex flex-col menu-drawer-in-right"
          >
            <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-200">
              <LayoutGrid className="w-4 h-4 text-purple-600" />
              <h2 className="text-sm font-semibold text-gray-900 flex-1">
                {t("flyout.title")}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("flyout.close")}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 touch-scroll">
              {/* Expanded, not compact: the drawer is dedicated space, so there
                  is nothing here for a collapsed widget to make room for. */}
              <WidgetRail forceCompact={false} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
